import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CompanyLinks, SupportLinks, ShopLinks } from '../components/FooterPopup';
import styles from './LandingPage.module.css';

const STATS = [
  { num: '50K+', label: 'Products listed' },
  { num: '10K+', label: 'Verified sellers' },
  { num: '2M+',  label: 'Happy customers' },
  { num: '4.8★', label: 'Average rating' },
];

const FEATURES = [
  {
    icon: '🛡️',
    title: 'Secure payments',
    desc: 'Every transaction is protected by Paystack — Nigeria\'s most trusted payment gateway. Your money is always safe.',
  },
  {
    icon: '🚀',
    title: 'Lightning delivery',
    desc: 'Same-day delivery in Lagos, Abuja and Port Harcourt. Next-day across all major Nigerian cities.',
  },
  {
    icon: '↩️',
    title: '7-day returns',
    desc: 'Changed your mind? Return anything within 7 days — no questions asked, full refund guaranteed.',
  },
  {
    icon: '✅',
    title: 'Verified sellers only',
    desc: 'Every seller goes through our strict verification process. No fakes, no scams — just quality products.',
  },
  {
    icon: '💬',
    title: '24/7 support',
    desc: 'Our customer care team is always available via chat, call or email to resolve any issue fast.',
  },
  {
    icon: '🏷️',
    title: 'Best price guarantee',
    desc: 'Find a lower price elsewhere? We\'ll match it. Vendly always has the best deals in Nigeria.',
  },
];

const CATEGORIES = [
  { emoji: '📱', label: 'Electronics',   count: '12,000+' },
  { emoji: '👗', label: 'Fashion',        count: '8,500+' },
  { emoji: '🏠', label: 'Home & Living',  count: '6,200+' },
  { emoji: '💄', label: 'Beauty',         count: '4,100+' },
  { emoji: '⚽', label: 'Sports',         count: '3,800+' },
  { emoji: '📚', label: 'Books',          count: '2,900+' },
  { emoji: '🍳', label: 'Kitchen',        count: '5,500+' },
  { emoji: '🔥', label: 'Hot Deals',      count: '1,200+' },
];

const TESTIMONIALS = [
  { name: 'Chioma A.', city: 'Lagos', text: 'I got my new phone delivered the same day I ordered. Vendly is unbeatable!', rating: 5 },
  { name: 'Emeka O.', city: 'Abuja', text: 'Best prices I\'ve found in Nigeria. The seller verification gives me peace of mind.', rating: 5 },
  { name: 'Fatima B.', city: 'Kano', text: 'Returns are actually hassle-free — I returned a dress and got my money back in 24hrs.', rating: 5 },
  { name: 'Tunde M.', city: 'PH', text: 'The app is so smooth. Shopping on Vendly feels premium but the prices are affordable.', rating: 5 },
];

const MARQUEE_ITEMS = ['Electronics', 'Fashion', 'Beauty', 'Home & Living', 'Sports', 'Deals', 'Kitchen', 'Books', 'Phones', 'Laptops', 'Sneakers', 'Skincare'];

// Intersection observer hook for scroll animations
function useInView(options = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold: 0.15, ...options });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function AnimatedSection({ children, className, delay = 0 }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`${className} ${styles.fadeUp} ${inView ? styles.fadeUpVisible : ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function LandingPage() {
  const [scrollY, setScrollY] = useState(0);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setActiveTestimonial(i => (i + 1) % TESTIMONIALS.length), 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className={styles.page}>

      {/* ── NAV ─────────────────────────────────────── */}
      <header className={`${styles.nav} ${scrollY > 40 ? styles.navScrolled : ''}`}>
        <div className={styles.navInner}>
          <Link to="/" className={styles.logo}>
            <span className={styles.logoIcon}>V</span>
            <span className={styles.logoText}>vendly</span>
          </Link>
          <nav className={styles.navLinks}>
            <Link to="/shop" className={styles.navLink}>Shop</Link>
            <a href="#features" className={styles.navLink}>Features</a>
            <a href="#categories" className={styles.navLink}>Categories</a>
          </nav>
          <div className={styles.navActions}>
            <Link to="/login" className={styles.navSignin}>Sign in</Link>
            <Link to="/register" className={styles.navRegister}>Get started</Link>
          </div>
        </div>
      </header>

      {/* ── HERO ────────────────────────────────────── */}
      <section className={styles.hero}>
        {/* Animated background blobs */}
        <div className={styles.blob1} />
        <div className={styles.blob2} />
        <div className={styles.blob3} />

        {/* Floating product cards */}
        <div className={styles.floatCard1}>
          <div className={styles.floatCardInner}>
            <span className={styles.floatEmoji}>📱</span>
            <div>
              <p className={styles.floatName}>iPhone 15 Pro</p>
              <p className={styles.floatPrice}>₦890,000</p>
            </div>
            <span className={styles.floatBadge}>In stock</span>
          </div>
        </div>
        <div className={styles.floatCard2}>
          <div className={styles.floatCardInner}>
            <span className={styles.floatEmoji}>👟</span>
            <div>
              <p className={styles.floatName}>Air Jordan 1</p>
              <p className={styles.floatPrice}>₦65,000</p>
            </div>
            <span className={styles.floatBadgeSale}>-30%</span>
          </div>
        </div>
        <div className={styles.floatCard3}>
          <div className={styles.floatCardInner}>
            <span className={styles.floatEmoji}>🎧</span>
            <div>
              <p className={styles.floatName}>Sony WH-1000XM5</p>
              <p className={styles.floatPrice}>₦85,000</p>
            </div>
            <span className={styles.floatBadge}>Top rated</span>
          </div>
        </div>

        <div className={styles.heroContent}>
          <div className={styles.heroBadge}>
            <span className={styles.heroBadgeDot} />
            🇳🇬 Nigeria's #1 marketplace
          </div>
          <h1 className={styles.heroTitle}>
            Shop everything<br />
            <span className={styles.heroTitleAccent}>you love.</span>
          </h1>
          <p className={styles.heroSub}>
            50,000+ products. Verified sellers. Fast delivery nationwide.
            Experience the smartest way to shop in Nigeria.
          </p>
          <div className={styles.heroCtas}>
            <Link to="/register" className={styles.ctaPrimary}>
              Start shopping free
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </Link>
            <Link to="/shop" className={styles.ctaSecondary}>
              Browse as guest
            </Link>
          </div>
          <div className={styles.heroTrust}>
            <div className={styles.heroAvatars}>
              {['🧑🏾', '👩🏽', '👨🏿', '👩🏾'].map((a, i) => (
                <span key={i} className={styles.heroAvatar}>{a}</span>
              ))}
            </div>
            <p className={styles.heroTrustText}>
              <strong>2M+ Nigerians</strong> already shopping on Vendly
            </p>
          </div>
        </div>
      </section>

      {/* ── MARQUEE ─────────────────────────────────── */}
      <div className={styles.marqueeWrap}>
        <div className={styles.marqueeTrack}>
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i} className={styles.marqueeItem}>
              {item} <span className={styles.marqueeDot}>•</span>
            </span>
          ))}
        </div>
      </div>

      {/* ── STATS ───────────────────────────────────── */}
      <section className={styles.statsSection}>
        <div className={styles.statsGrid}>
          {STATS.map((s, i) => (
            <AnimatedSection key={s.label} className={styles.statCard} delay={i * 100}>
              <span className={styles.statNum}>{s.num}</span>
              <span className={styles.statLabel}>{s.label}</span>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* ── CATEGORIES ──────────────────────────────── */}
      <section id="categories" className={styles.catsSection}>
        <AnimatedSection className={styles.sectionHead}>
          <p className={styles.sectionEyebrow}>Browse by category</p>
          <h2 className={styles.sectionTitle}>Find what you're looking for</h2>
          <p className={styles.sectionSub}>From the latest gadgets to everyday essentials — we have it all.</p>
        </AnimatedSection>
        <div className={styles.catsGrid}>
          {CATEGORIES.map((c, i) => (
            <AnimatedSection key={c.label} delay={i * 60}>
              <Link to={`/shop?cat=${c.label.toLowerCase()}`} className={styles.catCard}>
                <span className={styles.catEmoji}>{c.emoji}</span>
                <h3 className={styles.catLabel}>{c.label}</h3>
                <p className={styles.catCount}>{c.count} items</p>
                <span className={styles.catArrow}>→</span>
              </Link>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* ── FEATURES ────────────────────────────────── */}
      <section id="features" className={styles.featuresSection}>
        <AnimatedSection className={styles.sectionHead}>
          <p className={styles.sectionEyebrow}>Why Vendly</p>
          <h2 className={styles.sectionTitle}>Built for Nigerian shoppers</h2>
          <p className={styles.sectionSub}>We solve the real problems that make online shopping frustrating in Nigeria.</p>
        </AnimatedSection>
        <div className={styles.featuresGrid}>
          {FEATURES.map((f, i) => (
            <AnimatedSection key={f.title} className={styles.featureCard} delay={i * 80}>
              <span className={styles.featureIcon}>{f.icon}</span>
              <h3 className={styles.featureTitle}>{f.title}</h3>
              <p className={styles.featureDesc}>{f.desc}</p>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* ── HOW IT WORKS ────────────────────────────── */}
      <section className={styles.howSection}>
        <AnimatedSection className={styles.sectionHead}>
          <p className={styles.sectionEyebrow}>How it works</p>
          <h2 className={styles.sectionTitle}>Shop in 3 simple steps</h2>
        </AnimatedSection>
        <div className={styles.stepsRow}>
          {[
            { n: '01', title: 'Create your account', desc: 'Sign up free in under 60 seconds. No card required.' },
            { n: '02', title: 'Browse & add to cart', desc: 'Explore thousands of products and find the best deals.' },
            { n: '03', title: 'Pay & get delivered', desc: 'Checkout securely with Paystack. We deliver to your door.' },
          ].map((step, i) => (
            <AnimatedSection key={step.n} className={styles.step} delay={i * 150}>
              <span className={styles.stepNum}>{step.n}</span>
              <div className={styles.stepLine} />
              <h3 className={styles.stepTitle}>{step.title}</h3>
              <p className={styles.stepDesc}>{step.desc}</p>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* ── TESTIMONIALS ────────────────────────────── */}
      <section className={styles.testimonialSection}>
        <AnimatedSection className={styles.sectionHead}>
          <p className={styles.sectionEyebrow}>What people say</p>
          <h2 className={styles.sectionTitleLight}>Nigerians love Vendly</h2>
        </AnimatedSection>
        <div className={styles.testimonialsGrid}>
          {TESTIMONIALS.map((t, i) => (
            <div
              key={t.name}
              className={`${styles.testimonialCard} ${i === activeTestimonial ? styles.testimonialActive : ''}`}
            >
              <div className={styles.testimonialStars}>
                {'★'.repeat(t.rating)}
              </div>
              <p className={styles.testimonialText}>"{t.text}"</p>
              <div className={styles.testimonialAuthor}>
                <span className={styles.testimonialName}>{t.name}</span>
                <span className={styles.testimonialCity}>{t.city}</span>
              </div>
            </div>
          ))}
        </div>
        <div className={styles.testimonialDots}>
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              className={`${styles.dot} ${i === activeTestimonial ? styles.dotActive : ''}`}
              onClick={() => setActiveTestimonial(i)}
            />
          ))}
        </div>
      </section>

      {/* ── FINAL CTA ───────────────────────────────── */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaBlob1} />
        <div className={styles.ctaBlob2} />
        <AnimatedSection className={styles.ctaContent}>
          <h2 className={styles.ctaTitle}>Ready to start shopping?</h2>
          <p className={styles.ctaSub}>Join 2 million+ Nigerians. It's free, fast and secure.</p>
          <div className={styles.ctaBtns}>
            <Link to="/register" className={styles.ctaPrimaryLarge}>
              Create free account
            </Link>
            <Link to="/login" className={styles.ctaGhost}>
              Already have an account? Sign in →
            </Link>
          </div>
          <Link to="/shop" className={styles.ctaGuestLink}>
            Or browse without an account
          </Link>
        </AnimatedSection>
      </section>

      {/* ── FOOTER ──────────────────────────────────── */}
      <footer className={styles.footer}>
        <div className={styles.footerTop}>
          <div className={styles.footerBrand}>
            <div className={styles.footerLogo}>
              <span className={styles.logoIcon}>V</span>
              <span className={styles.logoText}>vendly</span>
            </div>
            <p className={styles.footerTagline}>
              Nigeria's most trusted online marketplace. Quality products, verified sellers, fast delivery.
            </p>
          </div>
          <ShopLinks />
          <CompanyLinks />
          <SupportLinks />
        </div>
        <div className={styles.footerBottom}>
          <span>© 2025 Vendly Technologies Ltd · Built with ♥ in Nigeria 🇳🇬</span>
          <span>RC: 1234567</span>
        </div>
      </footer>
    </div>
  );
}
